import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Card,
    CardContent,
    Divider,
    IconButton,
    CircularProgress,
    Tooltip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import RouteIcon from '@mui/icons-material/Route';
import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import MainMap from './MainMap';
import * as Api from "../network/prompts_api";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MapIcon from '@mui/icons-material/Map';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CopyAll, Save } from '@mui/icons-material';
import { getLoggedInUser, updateUser } from '../network/notes_api';
import { useLocation } from 'react-router-dom';
import UsageLimitPopup from './UsageLimitsPopup';
import BadRequestPopup from './BadRequestPopup';
import PromptTooLongPopup from './PromptTooLongPopup';
import ErrorPopup from './ErrorPopup';
import RouteSavedPopup from './RouteSavedPopup';


const PageContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: calc(90vh - 64px); // Adjusted for navbar height
  width: 95vw;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
`;

const TopPanel = styled(Box)`
  flex: 1;
  background-color: #ffffff;
  padding: 18px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.04);
  display: flex;
  border-radius: 8px;
  overflow:hidden;
  flex-direction: column;
  justify-content: space-between;
`;

const MapSection = styled(Box)`
  flex: 8;
`;

const PromptInputArea = styled(Box)`
  display: flex;
  align-items: center;
  width:100%;
  justify-content:center;
  display:flex;
  gap: 12px;
//   margin-top: 12px;
`;

const StatLabel = styled(Typography)`
  font-weight: 600;
  margin-bottom: 4px;
`;

const StatContent = styled(Typography)`
  margin-bottom: 12px;
  color: #555;
`;

const NotesPageLoggedInView = () => {
    const location = useLocation();
    const initialRoute = (location.state as { route?: Api.RouteResponse })?.route ?? null;
    const [routeData, setRouteData] = useState<Api.RouteResponse | null>(initialRoute);
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [usageLimitPopup, setUsageLimitPopup] = useState(false);
    const [badRequestPopup, setBadRequestPopup] = useState(false);
    const [promptTooLongPopup, setPromptTooLongPopup] = useState(false);
    const [errorPopup, setErrorPopup] = useState(false);
    const [routeSavedPopup, setRouteSavedPopup] = useState(false);

    async function findRoute() {
    if (!prompt.trim()) return;
    setLoading(true);
    try {
        const response = await fetch('/api/prompts?prompt=' + encodeURIComponent(prompt));
        
        if (!response.ok) {
        // Extract error message from response JSON
        const errorData = await response.json();
        if (response.status === 498) {
            setPromptTooLongPopup(true)
        }else if (response.status === 400){
            setBadRequestPopup(true)
        } else if (response.status === 403) {
            setUsageLimitPopup(true)
        } else if (response.status === 401) {
            alert("You're not authorized. Please log in.");
            window.location.reload();
        } else {
            setErrorPopup(true)
        }
        return; // stop further processing
        }
        
        const data = await response.json();
        setRouteData(data);
    } catch (error) {
        console.error("Failed to fetch route:", error);
        alert("Something went wrong.");
    } finally {
        setLoading(false);
    }
    }



function copyRouteToGoogleMapsLink(route: [number, number][]) {
  if (!route?.length) return;

  const MAX_WAYPOINTS = 10; // Safe for mobile

  // Downsample if too long
  const step = Math.max(1, Math.floor(route.length / MAX_WAYPOINTS));
  const sampled = route.filter((_, i) => i % step === 0);

  // Always include first and last points
  const coords = [
    sampled[0],
    ...sampled.slice(1, -1),
    sampled[sampled.length - 1],
  ];

  // Format for URL: lat,lng
  const url = `https://www.google.com/maps/dir/${coords
    .map(([lat, lng]) => `${lat},${lng}`)
    .join('/')}`;

  // Copy to clipboard
  navigator.clipboard.writeText(url)
    .then(() => alert("Google Maps link copied to clipboard!"))
    .catch((err) => {
      console.error("Clipboard error:", err);
      alert("Failed to copy the route.");
    });

  // Optional: auto open
  // window.open(url, "_blank");
}



    function openGoogleMapsNavRoute(route: [number, number][]) {
    if (route.length < 2) return;

    const origin = `${route[0][0]},${route[0][1]}`;
    const destination = `${route[route.length - 1][0]},${route[route.length - 1][1]}`;

    const maxWaypoints = 2; // fewer for mobile to avoid issues
    const interval = Math.floor((route.length - 2) / maxWaypoints);
    const waypoints: string[] = [];

    for (let i = 1; i < route.length - 1 && waypoints.length < maxWaypoints; i += interval) {
        waypoints.push(`${route[i][0]},${route[i][1]}`);
    }

    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${encodeURIComponent(waypoints.join('|'))}&travelmode=driving`;

    window.open(url, '_blank');
}

    async function saveRoute() {
    if (!routeData) {
        return;
    }

    try {
        const user = await getLoggedInUser();
        if (!user) {
        alert("User not logged in.");
        return;
        }

        // Define max saves per tier — match your tiers config
        const maxSavesByTier: Record<string, number> = {
        free: 1,
        pro: 10,
        premium: 25, // unlimited
        };

        const currentSavedCount = user.routes?.length || 0;
        const maxAllowed = maxSavesByTier[user.subscriptionStatus] ?? 0;

        if (currentSavedCount >= maxAllowed) {
        if (user.subscriptionStatus === "free") {
            setUsageLimitPopup(true)
        } else if (user.subscriptionStatus === "pro") {
            alert("Upgrade to Premium to save more than 10 routes.");
        } else {
            alert("You have reached your maximum saved routes limit.");
        }
        return;
        }

        // Add the new route and update user
        const updatedRoutes = [...(user.routes || []), routeData];
        await updateUser({ routes: updatedRoutes });

        setRouteSavedPopup(true)
    } catch (error) {
        console.error("Failed to save route:", error);
        alert("Failed to save route. Please try again.");
    }
    }



function generateGPX(route: [number, number][], name = "Route") {
  const points = route
    .map(([lat, lon]) => `
      <trkpt lat="${lat}" lon="${lon}">
        <time>${new Date().toISOString()}</time>
      </trkpt>`)
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>
<gpx version="1.1" creator="YourAppName" xmlns="http://www.topografix.com/GPX/1/1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.topografix.com/GPX/1/1
http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${name}</name>
    <time>${new Date().toISOString()}</time>
  </metadata>
  <trk>
    <name>${name}</name>
    <trkseg>
      ${points}
    </trkseg>
  </trk>
</gpx>`;
}

function downloadGPXFile(route: [number, number][]) {
  if (!route.length) return;

  const gpxData = generateGPX(route, "My Route");
  const blob = new Blob([gpxData], { type: "application/gpx+xml" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "route.gpx";
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}


function openGoogleMapsNavigationOld(route: [number, number][]) {
    if (!route.length) return;

    const maxWaypoints = 23; // Google's limit for non-API URLs
    const totalPoints = route.length;

    // Always include start and end
    const sampledPoints: [number, number][] = [];
    sampledPoints.push(route[0]);

    if (totalPoints > maxWaypoints) {
        const interval = Math.floor((totalPoints - 2) / (maxWaypoints - 2));
        for (let i = 1; i < totalPoints - 1; i += interval) {
            sampledPoints.push(route[i]);
            if (sampledPoints.length >= maxWaypoints - 1) break;
        }
    } else {
        sampledPoints.push(...route.slice(1, -1));
    }

    sampledPoints.push(route[route.length - 1]);

    // Format as lat,lng strings
    const coordStrings = sampledPoints.map(([lat, lng]) => `${lat},${lng}`);

    const url = `https://www.google.com/maps/dir/${coordStrings.join('/')}`;
    window.open(url, '_blank');
}

function openGoogleMapsNavigation(route: [number, number][]) {
    if (route.length < 2) return;

    const origin = `${route[0][0]},${route[0][1]}`;
    const destination = `${route[route.length - 1][0]},${route[route.length - 1][1]}`;

    const maxWaypoints = 3; // safe limit for mobile navigation
    const totalPoints = route.length;

    const waypoints: string[] = [];

    if (totalPoints > 2) {
        const interval = Math.floor((totalPoints - 2) / maxWaypoints);
        for (let i = 1; i < totalPoints - 1 && waypoints.length < maxWaypoints; i += interval) {
            waypoints.push(`${route[i][0]},${route[i][1]}`);
        }
    }

    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${encodeURIComponent(waypoints.join('|'))}&travelmode=driving`;

    window.open(url, '_blank');
}


    function downloadRoute(routeData: Api.RouteResponse | null) {
    if (!routeData || !routeData.route.length) return;

    const { route, description } = routeData;

    const kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
    <kml xmlns="http://www.opengis.net/kml/2.2">
        <Document>
        <name>Generated Route</name>
        <description>${description}</description>
        <Placemark>
            <name>Route Path</name>
            <LineString>
            <tessellate>1</tessellate>
            <coordinates>
                ${route.map(([lat, lng]) => `${lng},${lat},0`).join(' ')}
            </coordinates>
            </LineString>
        </Placemark>
        </Document>
    </kml>`;

    const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'route.kml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    }

    return (
        <PageContainer>
            <UsageLimitPopup isOpen={usageLimitPopup} onClose={()=>setUsageLimitPopup(false)}/>
            <BadRequestPopup isOpen={badRequestPopup} onClose={()=>setBadRequestPopup(false)}/>
            <PromptTooLongPopup isOpen={promptTooLongPopup} onClose={()=>setPromptTooLongPopup(false)}/>
            <ErrorPopup isOpen={errorPopup} onClose={()=>setErrorPopup(false)}/>
            <RouteSavedPopup isOpen={routeSavedPopup} onClose={()=>setRouteSavedPopup(false)}/>

            {/* Top Panel */}
            <TopPanel>
                <Box>
                    {/* <Typography variant="h5" fontWeight={600} gutterBottom>
                        Route Generator
                    </Typography> */}
                    {/* <Typography variant="body2" color="text.secondary" gutterBottom>
                        Describe your route and we’ll generate it for you.
                    </Typography> */}
                    <PromptInputArea>
                    <TextField

                        // fullWidth
                        style={{width:'100%', flex:16}}
                        variant="outlined"
                        placeholder="Plan me a route from London to Birmingham that avoids motorways and stops at a scenic location half way for lunch."
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        disabled={loading}
                    />
                    <Box display="flex" alignItems="center" gap={1} flex={1} justifyContent={'center'}>

                    <IconButton
                        color="primary"
                        onClick={findRoute}
                        disabled={loading || !prompt.trim()}
                    >
                        {loading ? <CircularProgress size={24} /> : <AutoAwesomeIcon />}
                    </IconButton>
                    </Box>
                    <Tooltip title="Open in Google Maps" arrow>
                    <MapIcon
                        onClick={() => routeData?.route ? openGoogleMapsNavigationOld(routeData?.route) : {}}
                        style={{ flex: 1, cursor: "pointer" }}
                    />
                    </Tooltip>

                    <Tooltip title="Save Route" arrow>
                    <Save
                        onClick={() => saveRoute()}
                        style={{ flex: 1, cursor: "pointer" }}
                    />
                    </Tooltip>

                    <Tooltip title="Download GPX" arrow>
                    <DownloadIcon
                        onClick={() => downloadGPXFile(routeData?.route ?? [])}
                        style={{ flex: 1, cursor: "pointer" }}
                    />
                    </Tooltip>
                </PromptInputArea>
                </Box>
            </TopPanel>

            {/* Map Section */}
            <div style={{display:'flex', width:'100%', height:'70vh'}}>
            <MapSection style={{maxHeight:'100%', padding:'10px', flex:2}}>
                <MainMap
                    routeData={routeData ?? undefined}
                    />

            </MapSection>
                <Card style={{overflow:'scroll', flex:1}} variant="outlined" sx={{ flexGrow: 1, my: 2 }}>
                    <CardContent >
                        {routeData ? (
                            <>
                                <StatLabel>Original prompt</StatLabel>
                                <StatContent onClick={()=>routeData.prompt ? setPrompt(routeData.prompt) : {}}>{routeData.prompt ?? 'No prompt'}</StatContent>
                                <StatLabel>About this route</StatLabel>
                                <StatContent>{routeData.subjective ?? 'No description'}</StatContent>
                                <StatLabel>Distance</StatLabel>
                                <StatContent>
                                {routeData.summary?.distance != null
                                    ? `${(routeData.summary.distance / 1000).toFixed(1)} km`
                                    : 'N/A'}
                                </StatContent>

                                <StatLabel>Duration</StatLabel>
                                <StatContent>
                                {routeData.summary?.duration != null
                                    ? `${Math.floor(routeData.summary.duration / 3600)}h ${Math.round((routeData.summary.duration % 3600) / 60)}m`
                                    : 'N/A'}
                                </StatContent>


                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No route loaded yet.
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </div>

        </PageContainer>
    );
};

export default NotesPageLoggedInView;
